import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

// Build a windowed page list: always first & last, current ±1, with '…' gaps.
export function pageWindow(page, totalPages) {
    const out = [];
    let prev;
    for (let i = 1; i <= totalPages; i++) {
        if (i === 1 || i === totalPages || (i >= page - 1 && i <= page + 1)) {
            if (prev && i - prev > 1) out.push('…');
            out.push(i);
            prev = i;
        }
    }
    return out;
}

/**
 * Shared pagination bar — the single source of truth for pagination styling across
 * the app (item range on the left, page numbers + prev/next on the right).
 *
 * Props:
 *   - page:         current page (1-based)
 *   - totalPages:   total number of pages
 *   - total:        total item count (drives the "showing X–Y of Z" text)
 *   - pageSize:     items per page
 *   - currentCount: number of items rendered on the current page
 *   - onPage:       (pageNumber) => void
 *   - className:    optional extra classes on the wrapper (e.g. spacing)
 */
export default function PaginationBar({ page, totalPages, total, pageSize, currentCount, onPage, className = '' }) {
    const { t, i18n } = useTranslation();
    const isBn = i18n.language?.startsWith('bn');
    if (!total) return null;

    const bn = (n) => (isBn ? String(n).replace(/[0-9]/g, (d) => '০১২৩৪৫৬৭৮৯'[d]) : String(n));
    const start = (page - 1) * pageSize + 1;
    const end = (page - 1) * pageSize + currentCount;
    const navBtn = 'w-9 h-9 flex items-center justify-center rounded-[9px] border border-[#ECE9E3] dark:border-zinc-700 bg-white dark:bg-zinc-900 text-[#1C1B19] dark:text-zinc-100 hover:bg-[#FAF9F6] dark:hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors';
    const pageBtn = 'min-w-9 h-9 px-2 flex items-center justify-center rounded-[9px] border text-[13px] font-semibold transition-colors';

    return (
        <div className={`flex flex-wrap items-center justify-between gap-3 ${className}`}>
            <span className="text-[13px] text-[#6E6A63] dark:text-zinc-300">
                {t('common.showingRange', { start: bn(start), end: bn(end), total: bn(total) })}
            </span>
            {totalPages > 1 && (
                <div className="flex items-center gap-1.5">
                    <button type="button" onClick={() => onPage(page - 1)} disabled={page === 1} className={navBtn} aria-label={t('common.previous')}>
                        <IconChevronLeft size={16} />
                    </button>
                    {pageWindow(page, totalPages).map((n, i) =>
                        n === '…' ? (
                            <span key={`d${i}`} className="px-1 text-[13px] text-[#C3BEB4] dark:text-zinc-600 select-none">…</span>
                        ) : (
                            <button
                                key={n}
                                type="button"
                                onClick={() => onPage(n)}
                                className={`${pageBtn} ${n === page
                                    ? 'border-[#E2702E] bg-[#E2702E] text-white'
                                    : 'border-[#ECE9E3] dark:border-zinc-700 bg-white dark:bg-zinc-900 text-[#1C1B19] dark:text-zinc-100 hover:bg-[#FAF9F6] dark:hover:bg-zinc-800'}`}
                            >
                                {bn(n)}
                            </button>
                        )
                    )}
                    <button type="button" onClick={() => onPage(page + 1)} disabled={page === totalPages} className={navBtn} aria-label={t('common.next')}>
                        <IconChevronRight size={16} />
                    </button>
                </div>
            )}
        </div>
    );
}
